import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Fetch } from '../../utils/Utils.js';
import { useTranslation } from 'react-i18next';
import { ResponsiveLine } from '@nivo/line';
import './WeightChart.css';
import '../../herd/ListHerd.css';
import '../reproduction/NivoChartCard.css';

import { formatDate } from '../../../utils/dateFormat.js';
import DateInput from '../../shared/inputs/DateInput.js';

// Each selected animal is its own identity, not a fixed small set — fixed
// categorical hue order (palette.md slots 1-8), assigned by selection order.
// Past 8 concurrently selected animals it cycles rather than folding to
// "Other" (an individual tag can't fold into a group bucket the way a
// category can), same compromise the original 10-color palette made.
const COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];

// Format "2026-01" to "Jan 2026" (or "fev. de 2026" in PT)
const formatMonthLabel = (yearMonth) => {
  const [year, month] = yearMonth.split('-');
  const date = new Date(year, parseInt(month) - 1);
  return formatDate(date, { month: 'short', year: 'numeric' });
};

export const WeightChart = ({ lotTagFilter }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [rawData, setRawData] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const availableTags = useRef([]);
  const tagDataMap = useRef({});

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    Fetch("/api/data/weights", "GET", null,
      (data) => {
        setRawData(data);
        processData(data);
      });
  }, []);

  // When lot filter changes, update selected tags to show animals in that lot
  useEffect(() => {
    if (!lotTagFilter) return;
    if (lotTagFilter.length === 0) {
      setSelectedTags([]);
      return;
    }
    const filtered = availableTags.current.filter(t => lotTagFilter.includes(t));
    setSelectedTags(filtered.slice(0, 10));
  }, [lotTagFilter]);

  const processData = (data) => {
    // Group data by tag and track last update time
    const tagMap = {};
    const tagLastUpdate = {};

    data.forEach(entry => {
      const tag = entry.tag;
      if (!tagMap[tag]) {
        tagMap[tag] = [];
        tagLastUpdate[tag] = entry.date;
      }
      tagMap[tag].push({
        date: entry.date,
        weight: entry.weight,
        yearMonth: entry.date?.slice(0, 7) || '',
      });
      // Track most recent update
      if (entry.date > tagLastUpdate[tag]) {
        tagLastUpdate[tag] = entry.date;
      }
    });

    // Sort each tag's entries by date
    Object.keys(tagMap).forEach(tag => {
      tagMap[tag].sort((a, b) => a.date.localeCompare(b.date));
    });

    tagDataMap.current = tagMap;
    availableTags.current = Object.keys(tagMap).sort((a, b) => a.localeCompare(b));

    // If navigated with a specific tag, show only that animal; otherwise default to 5 most recent
    const initialTag = location.state?.initialTag;
    if (initialTag && tagMap[String(initialTag)]) {
      setSelectedTags([String(initialTag)]);
    } else {
      const sortedByRecent = Object.keys(tagMap)
        .sort((a, b) => (tagLastUpdate[b] || '').localeCompare(tagLastUpdate[a] || ''))
        .slice(0, 5);
      setSelectedTags(sortedByRecent);
    }
  };

  const handleTagInputChange = (e) => {
    const value = e.target.value;
    setTagInput(value);

    if (value.trim()) {
      const pool = (lotTagFilter && lotTagFilter.length > 0)
        ? availableTags.current.filter(t => lotTagFilter.includes(t))
        : availableTags.current;
      const filtered = pool
        .filter(tag =>
          tag.toString().startsWith(value) &&
          !selectedTags.includes(tag)
        )
        .slice(0, 5);
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  };

  const addTag = useCallback((tag) => {
    if (!selectedTags.includes(tag) && availableTags.current.includes(tag)) {
      setSelectedTags(prev => [...prev, tag]);
    }
    setTagInput('');
    setSuggestions([]);
  }, [selectedTags]);

  const removeTag = (tag) => {
    setSelectedTags(prev => prev.filter(t => t !== tag));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      addTag(tagInput.trim());
    }
  };

  const activeFilterCount = [startDate, endDate].filter(Boolean).length;

  const resetFilters = () => {
    setStartDate('');
    setEndDate('');
  };

  // Filter entries by date range
  const filterByDateRange = (entries) => {
    if (!startDate && !endDate) return entries;

    return entries.filter(e => {
      const entryDate = e.date?.slice(0, 10) || '';
      if (startDate && entryDate < startDate) return false;
      if (endDate && entryDate > endDate) return false;
      return true;
    });
  };

  // Build chart data
  const buildChartData = () => {
    if (selectedTags.length === 0) {
      return [];
    }

    // Collect all unique months from selected tags (filtered by date range) —
    // shared x-domain for the point scale, even though each series only
    // supplies the months it actually has data for.
    const allMonths = new Set();
    selectedTags.forEach(tag => {
      const entries = filterByDateRange(tagDataMap.current[tag] || []);
      entries.forEach(e => {
        if (e.yearMonth) allMonths.add(e.yearMonth);
      });
    });
    const sortedMonths = Array.from(allMonths).sort();

    // Build one series per selected tag. Missing months are simply omitted
    // (not a null-valued point) so the line connects straight across a gap —
    // matches the original chart's spanGaps:true, unlike a trend chart where
    // a gap should show as "no data" (see ReproductionSuccessByMonthAndGroup).
    return selectedTags.map((tag, index) => {
      const entries = filterByDateRange(tagDataMap.current[tag] || []);
      const monthWeightMap = {};
      entries.forEach(e => {
        // If multiple weights in same month, use the latest
        monthWeightMap[e.yearMonth] = e.weight;
      });

      return {
        id: `${t('tag')} ${tag}`,
        color: COLORS[index % COLORS.length],
        data: sortedMonths
          .filter(month => monthWeightMap[month] !== undefined)
          .map(month => ({ x: formatMonthLabel(month), y: monthWeightMap[month] })),
      };
    });
  };

  const series = buildChartData();

  const animalCount = availableTags.current.length;
  const weightSummary = animalCount > 0
    ? t('chart_summary_weight_tracking', { count: animalCount })
    : t('chart_summary_no_weight_data');

  return (
    <div className="WeightChart Chart NivoCard">
      <h3>{t('weight_over_time')}</h3>
      <p className="chart-summary">{weightSummary}</p>
      {isMobile ? (
        <div className="mobile-search-filter">
          <div className="filter-item tag-filter" style={{ flex: 1, minWidth: 0 }}>
            <input
              className="mobile-search-input"
              type="text"
              value={tagInput}
              onChange={handleTagInputChange}
              onKeyDown={handleKeyDown}
              placeholder={t("find_tag")}
            />
            {suggestions.length > 0 && (
              <div className="suggestions-dropdown">
                {suggestions.map(tag => (
                  <div
                    key={tag}
                    className="suggestion-item"
                    onClick={() => addTag(tag)}
                  >
                    {tag}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            className={`mobile-filter-toggle ${filtersOpen ? 'open' : ''} ${activeFilterCount > 0 ? 'active' : ''}`}
            onClick={() => setFiltersOpen(o => !o)}
          >
            {t("filters")}
            {activeFilterCount > 0 && <span className="mobile-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      ) : (
        <div className="filters">
          <div className="filter-item tag-filter">
            <input
              type="text"
              value={tagInput}
              onChange={handleTagInputChange}
              onKeyDown={handleKeyDown}
              placeholder={t("find_tag")}
            />
            {suggestions.length > 0 && (
              <div className="suggestions-dropdown">
                {suggestions.map(tag => (
                  <div
                    key={tag}
                    className="suggestion-item"
                    onClick={() => addTag(tag)}
                  >
                    {tag}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DateInput
            value={startDate}
            onChange={(v) => setStartDate(v)}
            placeholder={t("start_date")}
            required={false}
            defaultToToday={false}
          />
          <DateInput
            value={endDate}
            onChange={(v) => setEndDate(v)}
            placeholder={t("end_date")}
            required={false}
            defaultToToday={false}
          />
        </div>
      )}

      {/* Selected tags */}
      {selectedTags.length > 0 && (
        <div className="selected-tags">
          {selectedTags.map((tag, index) => (
            <span
              key={tag}
              className="tag-badge"
              style={{ backgroundColor: COLORS[index % COLORS.length] }}
            >
              {tag}
              <button
                onClick={() => removeTag(tag)}
                className="tag-remove"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="chart-area">
        {selectedTags.length === 0 ? (
          <p className="empty-message">
            {t("select_animals_to_display")}
          </p>
        ) : (
          <div style={{ width: '100%', height: '100%' }}>
            <ResponsiveLine
              data={series}
              margin={{ top: 16, right: 24, bottom: 50, left: 60 }}
              xScale={{ type: 'point' }}
              yScale={{ type: 'linear', nice: true }}
              axisBottom={{ legend: t('month'), legendPosition: 'middle', legendOffset: 40, tickRotation: series.some(s => s.data.length > 6) ? -30 : 0 }}
              axisLeft={{ legend: t('weight_kg'), legendPosition: 'middle', legendOffset: -50, format: (v) => `${v}kg` }}
              colors={{ datum: 'color' }}
              lineWidth={2}
              pointSize={6}
              useMesh={true}
              enableGridX={false}
              motionConfig="gentle"
              theme={{
                axis: { ticks: { text: { fill: '#898781', fontSize: 12 } }, legend: { text: { fill: '#52514e', fontSize: 12.5 } } },
                grid: { line: { stroke: '#e1e0d9', strokeWidth: 1 } },
              }}
              tooltip={({ point }) => (
                <div className="nivo-tooltip">
                  <span className="nivo-tooltip-swatch" style={{ background: point.seriesColor }} />
                  <strong>{point.seriesId}</strong>: <span className="nivo-tooltip-value">{point.data.y} kg</span>
                </div>
              )}
            />
          </div>
        )}
      </div>

      {filtersOpen && (
        <>
          <div className="filter-sheet-backdrop" onClick={() => setFiltersOpen(false)} />
          <div className="filter-sheet">
            <div className="filter-sheet-handle" />
            <div className="filter-sheet-header">
              <span className="filter-sheet-title">{t("filters")}</span>
              <button className="filter-sheet-close" onClick={() => setFiltersOpen(false)}>✕</button>
            </div>
            <div className="filter-sheet-body">
              <div className="weight-chart-date-filters">
                <label>{t("start_date")}</label>
                <DateInput
                  value={startDate}
                  onChange={(v) => setStartDate(v)}
                  required={false}
                  defaultToToday={false}
                />
                <label>{t("end_date")}</label>
                <DateInput
                  value={endDate}
                  onChange={(v) => setEndDate(v)}
                  required={false}
                  defaultToToday={false}
                />
              </div>
            </div>
            <div className="filter-sheet-footer">
              <button
                className="filter-sheet-reset"
                onClick={resetFilters}
                disabled={activeFilterCount === 0}
              >
                {t("reset_filters")} {activeFilterCount > 0 && `(${activeFilterCount})`}
              </button>
              <button className="filter-sheet-apply" onClick={() => setFiltersOpen(false)}>
                {t("done")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WeightChart;
